import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { listProductListings } from '../../api/business/endpoints';
import type { ListingState } from '../../api/business/types';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { useLanguage } from '../../i18n/LanguageContext';

const STATE_COLOR: Record<ListingState, 'default' | 'success' | 'error'> = {
  PENDING: 'default',
  SENT: 'success',
  FAILED: 'error',
};

/** The e-shops a product is listed in (R0033): published or hidden there, and how its document went. */
export const ProductShopsSection = ({ companyId, productId }: { companyId: string; productId: string }) => {
  const { language, t } = useLanguage();
  const query = useQuery({
    queryKey: ['product-listings', companyId, productId],
    queryFn: () => listProductListings(companyId, productId),
  });

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6">{t('productShops.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('productShops.hint')}
      </Typography>
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      {query.data && query.data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('productShops.empty')}
        </Typography>
      ) : null}
      {(query.data ?? []).map((one, index) => (
        <Fragment key={one.linkId}>
          {index > 0 ? <Divider sx={{ my: 1.5 }} /> : null}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="subtitle1"
                component={RouterLink}
                to={`/integrations/${companyId}/${one.linkId}`}
                sx={{ color: 'inherit' }}
              >
                {one.linkName}
              </Typography>
              <Chip size="small" variant="outlined" label={t(`platform.${one.platform}`)} />
              <Chip size="small" label={one.published ? t('listings.published') : t('listings.hidden')} />
              <Chip size="small" color={STATE_COLOR[one.state]} label={t(`listingState.${one.state}`)} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {[
                one.shopProductId ? `${t('listings.shopId')} #${one.shopProductId}` : null,
                one.lastSentAt
                  ? `${t('listings.lastSent')}: ${formatDateTime(one.lastSentAt, language)}`
                  : null,
                one.skipped.length > 0 ? t('listings.skipped', { list: one.skipped.join(', ') }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            {one.state === 'FAILED' && one.lastError ? (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                {one.lastError}
              </Typography>
            ) : null}
          </Box>
        </Fragment>
      ))}
    </Paper>
  );
};
